import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { SERVER_URL } from "../config";
import SearchableDropdown from "../components/SearchableDropdown";
import {
    PlusIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    Bars3Icon,
} from "@heroicons/react/24/outline";

const AdminProducts = () => {
    const [products, setProducts] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [selectedService, setSelectedService] = useState("");
    const [search, setSearch] = useState("");
    const [activeTab, setActiveTab] = useState("ALL");
    const [showModal, setShowModal] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const [newProduct, setNewProduct] = useState({
        name: "",
    });

    /* ---------------- FETCH DATA ---------------- */
    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const res = await fetch(`${SERVER_URL}/Rahul/fetchProductAndCategory.php`);
            const data = await res.json();
            if (data.success) {
                setProducts(data.products.map((p) => ({
                    id: p.id, name: p.name, service: p.service, status: p.status,
                })));
                setServiceTypes(data.categories);
            }
        } catch (error) {
            console.error("Failed to fetch products", error);
        }
    };

    /* ---------------- ADD PRODUCT ---------------- */
    const handleAddProduct = async () => {
        if (!newProduct.name) return alert("Fill all fields");
        try {
            const res = await fetch(`${SERVER_URL}/Rahul/addNewProduct.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: newProduct.name }),
            });
            const data = await res.json();
            if (!data.success) throw new Error("Insert failed");
            setProducts([...products, { id: data.id, name: newProduct.name, status: "ACTIVE" }]);
            setNewProduct({ name: "" });
            setShowModal(false);
        } catch (err) {
            alert("Failed to add product");
            console.error(err);
        }
    };

    /* ---------------- STATUS TOGGLE ---------------- */
    const toggleStatus = async (id, currentStatus) => {
        const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        setProducts((prev) => prev.map((p) => p.id === id ? { ...p, status: newStatus } : p));
        try {
            const res = await fetch(`${SERVER_URL}/Rahul/updateProductStatus.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id, status: newStatus }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message);
        } catch (error) {
            alert("Failed to update status");
            setProducts((prev) => prev.map((p) => p.id === id ? { ...p, status: currentStatus } : p));
        }
    };

    /* ---------------- FILTER LOGIC ---------------- */
    const filteredProducts = products.filter((p) => {
        if (activeTab === "ACTIVE" && p.status !== "ACTIVE") return false;
        if (activeTab === "INACTIVE" && p.status !== "INACTIVE") return false;
        if (selectedService && p.service !== selectedService) return false;
        if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    const tabConfig = {
        ALL: { active: "bg-blue-50 text-blue-700 border-blue-300", idle: "text-slate-500 border-slate-200 hover:bg-blue-50 hover:text-blue-700" },
        ACTIVE: { active: "bg-emerald-50 text-emerald-700 border-emerald-300", idle: "text-slate-500 border-slate-200 hover:bg-emerald-50 hover:text-emerald-700" },
        INACTIVE: { active: "bg-rose-50 text-rose-700 border-rose-300", idle: "text-slate-500 border-slate-200 hover:bg-rose-50 hover:text-rose-700" },
    };

    /* ---------------- COUNTS ---------------- */
    // const counts = {
    //     ALL: products.length,
    //     ACTIVE: products.filter(p => p.status === "ACTIVE").length,
    //     INACTIVE: products.filter(p => p.status === "INACTIVE").length,
    // };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                * { box-sizing: border-box; }
                body { font-family: 'Inter', sans-serif; }
                .prod-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .prod-scrollbar::-webkit-scrollbar-track { background: rgba(15,23,42,0.05); border-radius:10px; }
                .prod-scrollbar::-webkit-scrollbar-thumb { background: rgba(15,23,42,0.18); border-radius:10px; }
                .prod-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(15,23,42,0.32); }
            `}</style>

            <div className="min-h-screen w-full font-[Inter]" style={{ background: "#f0f4f8" }}>

                {/* Subtle top accent line */}
                <div className="fixed top-0 left-0 right-0 h-0.5 z-50"
                    style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

                <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} onLogout={() => { localStorage.clear(); }} />

                {/* ── STICKY HEADER ── */}
                <div className="sticky top-0 z-20 border-b border-slate-200"
                    style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}>
                    <div className="px-7 py-3.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setIsSidebarOpen(true)}
                                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                            >
                                <Bars3Icon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2.5">
                                <div className="w-1 h-6 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                                    Product Management
                                </h1>
                            </div>
                        </div>
                        {/* <button
                            onClick={() => setShowModal(true)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white border-0 transition-all hover:opacity-90"
                            style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
                        >
                            <PlusIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">Add Product</span>
                        </button> */}
                    </div>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div className="relative z-10 p-5 flex flex-col gap-4">

                    {/* ── FILTER BAR ── */}
                    <div className="rounded-xl px-5 py-4 bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">

                            {/* Search + Category */}
                            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                                {/* Search */}
                                <div className="flex items-center gap-2 rounded-lg px-3 py-2 h-10 border border-slate-200 bg-slate-50">
                                    <MagnifyingGlassIcon className="h-4 w-4 text-slate-400 shrink-0" />
                                    <input
                                        type="text"
                                        placeholder="Search products..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none w-44"
                                    />
                                    {search && (
                                        <button onClick={() => setSearch("")} className="text-slate-400 hover:text-slate-600">
                                            <XMarkIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>

                                {/* Category Dropdown */}
                                {/* <div className="w-48">
                                    <SearchableDropdown
                                        options={[{ label: "All Categories", value: "" }, ...serviceTypes.map(cat => ({ label: cat, value: cat }))]}
                                        value={selectedService}
                                        onChange={setSelectedService}
                                        placeholder="All Categories"
                                    />
                                </div> */}
                            </div>

                            {/* Tabs */}
                            {/* <div className="flex gap-2">
                                {["ALL", "ACTIVE", "INACTIVE"].map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-200
                                            ${activeTab === tab
                                                ? tabConfig[tab].active
                                                : `border-transparent ${tabConfig[tab].idle}`
                                            }`}
                                    >
                                        {tab}
                                        {counts[tab] > 0 && (
                                            <span className="ml-1.5 text-[10px] font-bold opacity-60">
                                                ({counts[tab]})
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div> */}
                        </div>
                    </div>

                    {/* ── TABLE ── */}
                    <div className="rounded-xl overflow-hidden bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="overflow-x-auto overflow-y-auto max-h-[520px] prod-scrollbar">
                            <table className="min-w-full text-sm">
                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                    <tr>
                                        {["Product", "Status"].map(h => (
                                            <th key={h} className="px-6 py-3 text-center border-b border-slate-200">
                                                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProducts.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center py-12 text-slate-400 text-sm">No data found</td>
                                        </tr>
                                    ) : (
                                        filteredProducts.map((p) => (
                                            <tr key={p.id} className="border-t border-slate-100 transition-colors hover:bg-blue-50/60">

                                                {/* Product Name */}
                                                <td className="px-6 py-3.5 text-center">
                                                    <span className="text-slate-800 font-medium text-sm">{p.name}</span>
                                                </td>

                                                {/* Service / Category */}
                                                {/* <td className="px-6 py-3.5 text-center">
                                                    <span className="px-2.5 py-0.5 text-xs rounded-full font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                                        {p.service}
                                                    </span>
                                                </td> */}

                                                {/* Status Badge */}
                                                <td className="px-6 py-3.5 text-center">
                                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border
                                                        ${p.status === "ACTIVE"
                                                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                            : "bg-rose-50 text-rose-700 border-rose-200"
                                                        }`}>
                                                        {p.status}
                                                    </span>
                                                </td>

                                                {/* Toggle */}
                                                {/* <td className="px-6 py-3.5 text-center">
                                                    <button
                                                        onClick={() => toggleStatus(p.id, p.status)}
                                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none
                                                            ${p.status === "ACTIVE" ? "bg-emerald-500" : "bg-rose-500"}`}
                                                    >
                                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300
                                                            ${p.status === "ACTIVE" ? "translate-x-6" : "translate-x-1"}`}
                                                        />
                                                    </button>
                                                </td> */}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── ADD PRODUCT MODAL ── */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center"
                    style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}>
                    <div className="w-[90%] max-w-sm rounded-2xl p-6 space-y-5 bg-white border border-slate-200"
                        style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}>

                        {/* Header */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="w-1 h-5 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <h3 className="text-base font-bold text-slate-800">Add New Product</h3>
                            </div>
                            <button onClick={() => setShowModal(false)}
                                className="text-slate-400 hover:text-slate-600 transition-colors">
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Service Type */}
                        {/* <div>
                            <SearchableDropdown
                                label="Service Type"
                                options={serviceTypes.map(s => ({ label: s, value: s }))}
                                value={newProduct.serviceType}
                                onChange={(val) => setNewProduct({ ...newProduct, serviceType: val })}
                                placeholder="Select Service Type"
                            />
                        </div> */}

                        {/* Product Name */}

                        <input
                            type="text"
                            placeholder="Product Name"
                            value={newProduct.name}
                            onChange={(e) =>
                                setNewProduct({ ...newProduct, name: e.target.value })
                            }
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                        />

                        {/* Actions */}
                        <div className="flex justify-end gap-3 pt-1">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddProduct}
                                className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                                style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AdminProducts;